import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faPhone, faEnvelope, faClock } from '@fortawesome/free-solid-svg-icons';
import { faFacebookF, faTwitter, faInstagram, faLinkedinIn } from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import './ContactPage.css';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
    subject: '',
    message: ''
  });

  const [contactInfo, setContactInfo] = useState({
    'Our Office': null,
    'Phone': null,
    'Email': null,
    'Working Hours': null,
    'Connect With Us': null
  });

  const [submitStatus, setSubmitStatus] = useState({
    message: '',
    type: ''
  });

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const response = await axios.get('https://realestate.bonlineco.com/api/contact-information');
      if (response.data.status === 'success') {
        const info = {};
        response.data.data.forEach(item => {
          if (item.is_visible) {
            info[item.type] = item;
          }
        });
        setContactInfo(info);
      }
    } catch (error) {
      console.error('Failed to fetch contact information:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ message: '', type: '' });

    try {
      const response = await axios.post('https://realestate.bonlineco.com/api/contact', formData);
      if (response.data.status === 'success') {
        setSubmitStatus({
          message: 'Message sent successfully!',
          type: 'success'
        });
        setFormData({
          name: '',
          email: '',
          phone_number: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      setSubmitStatus({
        message: error.response?.data?.message || 'Failed to send message. Please try again.',
        type: 'error'
      });
    }
  };

  return (
    <div className="contact-page">
      {/* Contact Header */}
      <section className="contact-header">
        <div className="container">
          <div className="row">
            <div className="col-md-12 text-center">
              <h1>Get in Touch</h1>
              <p className="lead">We're here to help you find your perfect home</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="contact-info py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-7">
              <div className="card mb-4" id='contact-container'>
                <div className="card-body">
                  <h2 style={{ color: '#a87758' }}>Send us a Message</h2>
                  <p className="text-muted mb-4">
                    Have questions about our properties? We're here to help you make the right choice.
                  </p>
                  
                  {submitStatus.message && (
                    <div className={`alert alert-${submitStatus.type === 'success' ? 'success' : 'danger'} mb-4`}>
                      {submitStatus.message}
                    </div>
                  )}
                  
                  <form className="contact-form" onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <div className="form-floating">
                          <input 
                            type="text" 
                            className="form-control" 
                            id="name" 
                            placeholder="Your Name"
                            value={formData.name}
                            onChange={handleChange}
                            required 
                          />
                          <label htmlFor="name">Your Name</label>
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <div className="form-floating">
                          <input 
                            type="email" 
                            className="form-control" 
                            id="email" 
                            placeholder="Email Address"
                            value={formData.email}
                            onChange={handleChange}
                            required 
                          />
                          <label htmlFor="email">Email Address</label>
                        </div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="form-floating">
                        <input 
                          type="tel" 
                          className="form-control" 
                          id="phone_number" 
                          placeholder="Phone Number"
                          value={formData.phone_number}
                          onChange={handleChange}
                        />
                        <label htmlFor="phone_number">Phone Number</label>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="form-floating">
                        <input 
                          type="text" 
                          className="form-control" 
                          id="subject" 
                          placeholder="Subject"
                          value={formData.subject}
                          onChange={handleChange}
                        />
                        <label htmlFor="subject">Subject</label>
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="form-floating">
                        <textarea 
                          className="form-control" 
                          id="message" 
                          placeholder="Your Message" 
                          rows="5"
                          value={formData.message}
                          onChange={handleChange}
                          required
                        ></textarea>
                        <label htmlFor="message">Your Message</label>
                      </div>
                    </div>
                    <button type="submit" className="btn btn-primary">Send Message</button>
                  </form>
                </div>
              </div>
            </div>
            
            <div className="col-lg-5">
              <div className="card merged-contact-card" id='contact-container'>
                <div className="card-body">
                  <h2 style={{ color: '#a87758' }}>Contact Information</h2>
                  <div className="contact-details">
                    {contactInfo['Our Office'] && (
                      <div className="contact-item">
                        <div className="icon-container">
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                        </div>
                        <div className="contact-text">
                          <h5>Our Office</h5>
                          <p>{contactInfo['Our Office'].value}</p>
                        </div>
                      </div>
                    )}

                    {contactInfo['Phone'] && (
                      <div className="contact-item">
                        <div className="icon-container">
                          <FontAwesomeIcon icon={faPhone} />
                        </div>
                        <div className="contact-text">
                          <h5>Phone</h5>
                          <p>{contactInfo['Phone'].value}</p>
                        </div>
                      </div>
                    )}

                    {contactInfo['Email'] && (
                      <div className="contact-item">
                        <div className="icon-container">
                          <FontAwesomeIcon icon={faEnvelope} />
                        </div>
                        <div className="contact-text">
                          <h5>Email</h5>
                          <p>{contactInfo['Email'].value}</p>
                        </div>
                      </div>
                    )}

                    {contactInfo['Working Hours'] && (
                      <div className="contact-item">
                        <div className="icon-container">
                          <FontAwesomeIcon icon={faClock} />
                        </div>
                        <div className="contact-text">
                          <h5>Working Hours</h5>
                          <p>{contactInfo['Working Hours'].value}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {contactInfo['Connect With Us'] && (
                    <div className="connect-section">
                      <h3>Connect With Us</h3>
                      <p className="text-muted mb-3">Follow us on social media for the latest updates</p>
                      <div className="social-links">
                        {contactInfo['Connect With Us'].social_links?.facebook && (
                          <a href={contactInfo['Connect With Us'].social_links.facebook} 
                             target="_blank" 
                             rel="noopener noreferrer" 
                             className="social-link">
                            <div className="icon-circle">
                              <FontAwesomeIcon icon={faFacebookF} />
                            </div>
                            <span>Facebook</span>
                          </a>
                        )}
                        {contactInfo['Connect With Us'].social_links?.twitter && (
                          <a href={contactInfo['Connect With Us'].social_links.twitter} 
                             target="_blank" 
                             rel="noopener noreferrer" 
                             className="social-link">
                            <div className="icon-circle">
                              <FontAwesomeIcon icon={faTwitter} />
                            </div>
                            <span>Twitter</span>
                          </a>
                        )}
                        {contactInfo['Connect With Us'].social_links?.instagram && (
                          <a href={contactInfo['Connect With Us'].social_links.instagram} 
                             target="_blank" 
                             rel="noopener noreferrer" 
                             className="social-link">
                            <div className="icon-circle">
                              <FontAwesomeIcon icon={faInstagram} />
                            </div>
                            <span>Instagram</span>
                          </a>
                        )}
                        {contactInfo['Connect With Us'].social_links?.linkedin && (
                          <a href={contactInfo['Connect With Us'].social_links.linkedin} 
                             target="_blank" 
                             rel="noopener noreferrer" 
                             className="social-link">
                            <div className="icon-circle">
                              <FontAwesomeIcon icon={faLinkedinIn} />
                            </div>
                            <span>LinkedIn</span>
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body p-0">
                  <h2 className="p-4 mb-0" style={{ color: '#a87758' }}>Visit Our Office</h2>
                  <div className="map-placeholder" style={{ height: '400px' }}>
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d23514.627993722356!2d39.2342564!3d21.5597994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x15c3cd4a9d84e11f%3A0x63eb2fa179d37c5b!2zRzZWSCs3WFcsINin2YTYudmD2Kkg2KjYp9mEINin2YTYtdmG2KzYjCDZhdit2KfYs9iMINin2YTYt9mK2YLYp9mB!5e0!3m2!1sen!2ssa!4v1709466548901"
                      width="100%"
                      height="400"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section py-5">
        <div className="container">
          <div className="row">
            <div className="col-md-12 text-center mb-5">
              <h2 style={{ color: '#a87758' }}>Frequently Asked Questions</h2>
              <p className="text-muted">Find quick answers to common questions about our services</p>
            </div>
          </div>
          
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="accordion" id="faqAccordion">
                <div className="accordion-item">
                  <h2 className="accordion-header" id="headingOne">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne">
                      How do I schedule a property viewing?
                    </button>
                  </h2>
                  <div id="collapseOne" className="accordion-collapse collapse" aria-labelledby="headingOne" data-bs-parent="#faqAccordion">
                    <div className="accordion-body">
                      You can schedule a property viewing by contacting us directly through phone or email, or by using the scheduling form on the property detail page. Our team will confirm your appointment and arrange the viewing at your convenience.
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item">
                  <h2 className="accordion-header" id="headingTwo">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo">
                      What documents do I need to rent a property?
                    </button>
                  </h2>
                  <div id="collapseTwo" className="accordion-collapse collapse" aria-labelledby="headingTwo" data-bs-parent="#faqAccordion">
                    <div className="accordion-body">
                      To rent a property, you typically need to provide identification (passport or driver's license), proof of income (pay stubs or bank statements), employment verification, references from previous landlords, and sometimes a credit check authorization.
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item">
                  <h2 className="accordion-header" id="headingThree">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree">
                      How much is the typical down payment for buying a home?
                    </button>
                  </h2>
                  <div id="collapseThree" className="accordion-collapse collapse" aria-labelledby="headingThree" data-bs-parent="#faqAccordion">
                    <div className="accordion-body">
                      The typical down payment can vary based on the type of loan and property. Conventional loans usually require 10-20% down payment, while FHA loans may require as little as 3.5%. We can help you understand your options and find the best financing solution for your situation.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
